import grayCloud from "../../../src/assets/gray_cloud.svg"
import { forwardRef, useImperativeHandle, useRef } from "react";
import { files } from "./file";
import { fileToBase64 } from "./fileUpload";
import { cn } from "./utils";
import { nanoid } from "nanoid";

const DefaultTemplate = forwardRef(function DefaultTemplate(props, ref) {
  const acceptTypes = props.acceptTypes;

  return (
    <div className="w-[415px] h-[128px] flex flex-col items-center " onClick={props.onClick}>
      <img src={grayCloud} alt="" />
      <div className="flex flex-col items-center gap-[12px] w-[100%]">
        <div className="flex text-[#333] text-center font-[Segoe UI]  font-semibold leading-[24px]">
          <div className="">Drag & drop files or &nbsp;</div>
          <div  className="text-[#224b99] underline cursor-pointer">
            Browse
          </div>
        </div>

        {!!acceptTypes?.length && (
          <div className="flex text-[#818181] font-[Segoe UI] text-[16px] leading-[24px]">
            <div className="text-center font-[300]">Supported formats : &nbsp;</div>
            {files
              .filter((d) => acceptTypes.includes(d.value))
              .map((d) => d.label)
              .join(", ")}
          </div>
        )}
      </div>
    </div>
  );
});

const Upload = forwardRef(function Upload(props, ref) {
  const triggerRef = useRef();
  const containerRef = useRef();
  const acceptTypes = [].concat(props.acceptTypes || []);

  async function handleClick() {
    triggerRef.current.click();
    await new Promise((resolve) => {
      triggerRef.current.onchange = (e) => {
        props.onProgress?.(true);
        resolve(fetchFiles(triggerRef.current.files));
        e.target.value = null;
      };
    }).then((files) => {
      props.onChange?.(files);
      props.onProgress?.(false);
    });
  }

  useImperativeHandle(ref, () => {
    return {
      mount() {
        handleClick();
      },
      unmount() {
        triggerRef.current.files = null;
        triggerRef.current.value = null;
        props.onProgress?.(false);
        props.onChange?.(null);
      },
    };
  });

  function handleDragEnter(e) {
    e.preventDefault();
    e.stopPropagation();
    containerRef.current.style.border = "1px solid #000";
  }

  function handleDragLeave(e) {
    e.preventDefault();
    e.stopPropagation();
    containerRef.current.style.border = "none";
  }

  async function handleDrop(e) {
    e.preventDefault();
    e.stopPropagation();
    containerRef.current.style.border = "none";
    props.onPending?.(true);
    props.onChange?.(await fetchFiles(e.dataTransfer.files ?? null));
    props.onPending?.(false);
  }

  return (
    <div
      ref={containerRef}
      className={cn("w-[100%] h-[100%] flex items-center justify-center", props.className || "")}
      style={{ ...props.style }}
      onDragOver={handleDragEnter}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {props.template || <DefaultTemplate acceptTypes={acceptTypes} onClick={handleClick} />}

      <input
        ref={triggerRef}
        type="file"
        name="fileuploader"
        hidden
        multiple={props.multiple}
        accept={files
          .filter((d) => acceptTypes.includes(d.value))
          .map((d) => d.mimeType)
          .join(",")}
      />
    </div>
  );
});

export async function fetchFiles(files) {
  if (!files?.length) return null;
  const _files = Object.values(files);
  return await Promise.all(_files.map((file) => fetchFile(file)));
}

export async function fetchFile(file) {
  return {
    file:file,
    data: await fileToBase64(file),
    id: nanoid(20),
    name: file.name,
    size: file.size,
    lastModified: file.lastModified,
    type: file.type,
  };
}

export default Upload;
